import { Box, ListItem, ListItemIcon, ListItemText } from "@mui/material";

import BaseEntitiesSearchAutocomplete, {
  BaseEntitiesSearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitiesSearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { AutocompleteOptionType } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { RepairWorkDto, RepairWorkGetPaginatedDto } from "@/core/api/generated/v1";

import AppTypography from "../../Text/AppTypography";
import { repairWorkToOption } from "./RepairWorkAutocomplete";

export interface RepairWorksAutocompleteProps extends BaseEntitiesSearchAutocompleteInheritableProps<RepairWorkDto> {
  searchFilters?: {
    repairWorkId: string;
  } & Partial<Omit<RepairWorkGetPaginatedDto, "search" | "includeIds">>;
}

export default function RepairWorksAutocomplete({
  isPreload = true,
  searchFilters,
  ...otherProps
}: RepairWorksAutocompleteProps) {
  return (
    <BaseEntitiesSearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={repairWorkToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.repairWorkApi.apiV1RepairWorkGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          repairWorkGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          repairWorkGetPaginatedDto: {
            ...params.repairWorkGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Repair work'
      placeholder='Search for repair work...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='repairWork' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <AppTypography
                    ellipsing={{ enabled: true }}
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </AppTypography>
                </>
              }
              secondary={
                option.data && (
                  <Box>
                    <AppTypography ellipsing={{ enabled: true }} variant='body2'>
                      {option.data?.description}
                    </AppTypography>
                  </Box>
                )
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
